import React, { FC } from 'react';
import styled from '@emotion/styled';
import { Box, Button } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useButton } from '~Hooks';
import { CompleteBongQuery_venue_company_categories } from '~Scenes/POS/Queries/__queries__';

const CategoryRow = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  background-color: var(--colorBlueGray1100);
  padding: 16px;
  min-height: 64px;
  border-bottom: 1px solid var(--colorBlueGray1200);

  &:first-of-type {
    border-radius: 8px 8px 0 0;
  }

  &:last-of-type {
    border-radius: 0 0 8px 8px;
    border-bottom: none;
  }

  &:only-of-type {
    border-radius: 8px;
  }
`;

const ColorBox = styled.div<{ bgColor: string }>`
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 6px;
  background-color: ${({ bgColor }) => bgColor || 'var(--colorBlueGray900)'};
`;

const NameColumn = styled(Box)`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-left: 16px;
`;

const IndicatorBall = styled(Box)`
  border-radius: 50%;
  width: 12px;
  height: 12px;
`;

interface Props {
  category: CompleteBongQuery_venue_company_categories;
  isOutOfStock: boolean;
  onToggleStock: () => void;
  i18n: (val: any) => string;
}

const CategoryRowItem: FC<Props> = ({ category, isOutOfStock, onToggleStock, i18n }) => {
  const stockButton = useButton({
    onClick: onToggleStock,
  });

  return (
    <CategoryRow>
      <Box display="flex" alignItems="center" style={{ flex: 1 }}>
        <ColorBox bgColor={category.color} />
        <NameColumn>
          <Heading level={5} color="var(--colorWhite)" bottom={0}>
            {i18n(category.nameLang)}
          </Heading>
          <Paragraph level={5} color="var(--colorBlueGray200)">
            {category.items.length} {category.items.length === 1 ? i18n('Inventory.ProductSingular') : i18n('Inventory.ProductPlural')}
          </Paragraph>
        </NameColumn>
      </Box>

      <Box
        display="inline-flex"
        alignItems="center"
        style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
        <IndicatorBall
          backgroundColor={isOutOfStock ? 'colorStatusRed' : 'colorStatusGreen'}
          marginRight="xs"
        />
        <Paragraph level={5} color="var(--colorBlueGray200)">
          {isOutOfStock ? i18n('Inventory.OutOfStock') : i18n('Inventory.InStock')}
        </Paragraph>
      </Box>

      {isOutOfStock ? (
        <Button {...stockButton} padding="small">
          {i18n('Inventory.RemoveOutOfStock')}
        </Button>
      ) : (
        <Button {...stockButton} padding="small">
          {i18n('Inventory.SetOutOfStock')}
        </Button>
      )}
    </CategoryRow>
  );
};

export default CategoryRowItem;
